import {React, styled} from 'common'
import {Colors} from 'appearance'
import {useState, useEffect} from 'react'
import Page from 'components/Page/Page'
import GeneralSettings from './Settings/GeneralSettings'
import InfoSettings from './Settings/InfoSettings'
import QueueSettings from './Settings/QueueSettings'
import ProfileSettings from './Settings/ProfileSettings'
import {useGetSettingsQuery, useSetSettingsMutation} from 'store/Store'
import Button from 'components/Control/Button'
import {FormProvider, useForm} from 'react-hook-form'
// FormProvider is used inside renderContent (general tab only)
import {onSubmit} from 'services/utils/toastUtils'
import {
    Box,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Tab,
    Tabs,
    Typography,
} from '@mui/material'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import InfoRoundedIcon from '@mui/icons-material/InfoRounded'
import StorageRoundedIcon from '@mui/icons-material/StorageRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import {generalSettingsValidation} from 'constants/FormField/settings'
import {zodResolver} from '@hookform/resolvers/zod'
import {useTranslation} from 'react-i18next'
import usePageLoader from 'logic/usePageLoader'
import useResponsive from 'logic/useResponsive'

const TABS = [
    {key: 'general', icon: <TuneRoundedIcon fontSize="small" />, translationKey: 'settings.general'},
    {key: 'profile', icon: <PersonRoundedIcon fontSize="small" />, translationKey: 'profile.title'},
    {key: 'info', icon: <InfoRoundedIcon fontSize="small" />, translationKey: 'settings.info'},
    {key: 'queues', icon: <StorageRoundedIcon fontSize="small" />, translationKey: 'settings.queues'},
]

const Settings = () => {
    const {t} = useTranslation()
    const {data: settings, isLoading} = useGetSettingsQuery()
    const {setIsLoading} = usePageLoader()
    const [activeTab, setActiveTab] = useState('general')

    const methods = useForm({
        resolver: zodResolver(generalSettingsValidation),
        defaultValues: settings,
    })
    const {reset, handleSubmit} = methods
    const [doSetByKeyConfig] = useSetSettingsMutation()

    const submitEdit = handleSubmit((itemData) =>
        onSubmit({
            itemData,
            itemName: 'setting',
            doSomething: doSetByKeyConfig,
            commandType: 'edit',
            t,
        })
    )

    useEffect(() => {
        if (settings) reset(settings)
    }, [settings, reset])

    useEffect(() => {
        setIsLoading(isLoading)
    }, [isLoading, setIsLoading])

    const {smallScreen} = useResponsive()
    const activeTabDef = TABS.find((tab) => tab.key === activeTab)
    const activeTabIndex = TABS.findIndex((tab) => tab.key === activeTab)

    const renderContent = () => {
        switch (activeTab) {
            case 'general':
                return (
                    <FormProvider {...methods}>
                        <GeneralSettings />
                    </FormProvider>
                )
            case 'profile':
                return <ProfileSettings />
            case 'info':
                return <InfoSettings />
            case 'queues':
                return <QueueSettings />
            default:
                return null
        }
    }

    const content = (
        <ContentCard>
            {/* Mobile: tab bar header */}
            {smallScreen ? (
                <Tabs
                    value={activeTabIndex}
                    onChange={(_, i) => setActiveTab(TABS[i].key)}
                    variant="scrollable"
                    scrollButtons="auto"
                    indicatorColor="primary"
                    textColor="primary">
                    {TABS.map((tab) => (
                        <Tab
                            key={tab.key}
                            icon={tab.icon}
                            label={t(tab.translationKey)}
                            iconPosition="start"
                            sx={{fontSize: 13, minHeight: 48}}
                        />
                    ))}
                </Tabs>
            ) : (
                <ContentHeader>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{color: 'primary.main', display: 'flex', alignItems: 'center'}}>
                            {activeTabDef?.icon}
                        </Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {t(activeTabDef?.translationKey)}
                        </Typography>
                    </Box>
                </ContentHeader>
            )}
            <Divider />
            <ContentBody>{renderContent()}</ContentBody>
            {activeTab === 'general' && (
                <>
                    <Divider />
                    <ContentFooter>
                        <Button onClick={submitEdit} variant="contained">
                            {t('save')}
                        </Button>
                    </ContentFooter>
                </>
            )}
        </ContentCard>
    )

    return (
        <Page>
            {smallScreen ? (
                <MobileLayout>{content}</MobileLayout>
            ) : (
                <SettingsLayout>
                    <Sidebar>
                        <SidebarLabel>{t('header.profile.settings')}</SidebarLabel>
                        <List disablePadding>
                            {TABS.map((tab) => {
                                const isActive = activeTab === tab.key
                                return (
                                    <ListItemButton
                                        key={tab.key}
                                        selected={isActive}
                                        onClick={() => setActiveTab(tab.key)}
                                        sx={{
                                            borderRadius: '8px',
                                            mb: 0.5,
                                            px: 1.5,
                                            py: 1,
                                            '&.Mui-selected': {
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                '&:hover': {bgcolor: 'primary.dark'},
                                                '& .MuiListItemIcon-root': {color: 'white'},
                                            },
                                        }}>
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 32,
                                                color: isActive ? 'white' : 'text.secondary',
                                            }}>
                                            {tab.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={t(tab.translationKey)}
                                            primaryTypographyProps={{
                                                fontSize: 14,
                                                fontWeight: isActive ? 600 : 400,
                                            }}
                                        />
                                    </ListItemButton>
                                )
                            })}
                        </List>
                    </Sidebar>
                    <ContentArea>{content}</ContentArea>
                </SettingsLayout>
            )}
        </Page>
    )
}

export default Settings

const MobileLayout = styled.div`
    width: 100%;
    height: 100%;
    overflow-y: auto;
    padding-bottom: 16px;
`

const SettingsLayout = styled.div`
    display: flex;
    gap: 20px;
    width: 100%;
    height: 100%;
    overflow: hidden;
`

const Sidebar = styled.div`
    width: 190px;
    flex-shrink: 0;
    padding-top: 4px;
`

const SidebarLabel = styled.p`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: ${Colors.APP_FADED_GREY};
    margin: 0 0 10px 6px;
`

const ContentArea = styled.div`
    flex: 1;
    overflow-y: auto;
    min-width: 0;
    padding-bottom: 16px;
`

const ContentCard = styled(Paper)`
    border-radius: 12px !important;
    border: 1px solid ${Colors.LIGHT_GRAY} !important;
    overflow: hidden;
    box-shadow: none !important;
`

const ContentHeader = styled.div`
    padding: 16px 20px;
`

const ContentBody = styled.div`
    padding: 8px 20px 20px;
`

const ContentFooter = styled.div`
    padding: 12px 20px;
    display: flex;
    justify-content: flex-end;
    background: ${Colors.background};
`
